import { useEffect, useState } from "react";
import { Lightbulb, ChevronDown, Loader2, ScrollText } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useI18n } from "@/i18n/I18nProvider";
import { DEFAULT_LANG } from "@/i18n/languages";
import { StatusBadge } from "@/components/app/ui";

const List = ({ items }) => <ol className="list-decimal space-y-1 pl-5">{items.map((s) => <li key={s}>{s}</li>)}</ol>;

function Section({ label, children, testId }) {
  if (!children || (Array.isArray(children) && !children.length)) return null;
  return <div data-testid={testId}><div className="label-caps mb-1">{label}</div><div className="text-sm leading-relaxed text-slate-800">{children}</div></div>;
}

export function ExplainSimply({ kind, id, status, defaultOpen = false }) {
  const { lang, t } = useI18n();
  const [open, setOpen] = useState(defaultOpen);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { setData(null); setErr(""); }, [kind, id, lang]);
  useEffect(() => {
    if (!open || data || !id) return;
    api.get(`/i18n/explain/${kind}/${id}`, { params: { lang } }).then((r) => setData(r.data)).catch((e) => setErr(errMsg(e)));
  }, [open, data, kind, id, lang]);
  const e = data?.explanation;
  return (
    <section className="rounded-lg border border-emerald-200 bg-emerald-50/40" data-testid={`explain-simply-${kind}`}>
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="flex w-full items-center justify-between gap-2 px-5 py-3 text-left" data-testid="explain-simply-toggle">
        <span className="flex items-center gap-2 font-display text-sm font-semibold text-emerald-900"><Lightbulb className="h-4 w-4" />{t("Explain in Simple Terms")}</span>
        <ChevronDown className={`h-4 w-4 text-emerald-700 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-4 border-t border-emerald-100 px-5 py-4">
          {!data && !err && <p className="flex items-center gap-2 text-sm text-slate-500" data-testid="explain-loading"><Loader2 className="h-4 w-4 animate-spin" />{t("Preparing a simple explanation…")}</p>}
          {err && <p className="text-sm text-amber-700" data-testid="explain-error">{err}</p>}
          {e && (<>
            <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="explain-official">
              <div className="label-caps mb-1 flex items-center gap-1.5"><ScrollText className="h-3.5 w-3.5" />{t("Requirement — Official / Source Text")}</div>
              <p className="text-sm text-slate-800" data-no-translate lang="en-IN">{data.official.requirement}</p>
              <p className="mt-1 text-[11px] text-slate-500" data-no-translate>{data.official.source} · {data.official.section}{data.official.basis ? ` · ${data.official.basis}` : ""}</p>
              {lang !== DEFAULT_LANG && <p className="mt-2 border-t pt-2 text-sm text-slate-600"><span className="label-caps mr-1">{t("Reference translation")}:</span>{data.official.requirement}</p>}
            </div>
            <div className="space-y-4" data-no-translate lang={lang}>
              <Section label={t("What This Means")} testId="explain-what-it-means">{e.what_it_means}</Section>
              <Section label={t("What You Need to Do")} testId="explain-what-to-do">{e.what_to_do?.length ? <List items={e.what_to_do} /> : null}</Section>
              <div className="grid gap-4 md:grid-cols-2">
                <Section label={t("Who Should Handle This")} testId="explain-who">{e.who_should_handle?.join(", ")}</Section>
                <Section label={t("Evidence Required")} testId="explain-evidence">{e.evidence_required?.length ? <List items={e.evidence_required} /> : null}</Section>
              </div>
              <Section label={t("Risk if Not Addressed")} testId="explain-risk">{e.risk_if_not_addressed}</Section>
              {status && <div><div className="label-caps mb-1">{t("Status")}</div><StatusBadge status={status} /></div>}
              <Section label={t("Recommended Next Step")} testId="explain-next-step">{e.recommended_next_step}</Section>
            </div>
            <p className="text-[11px] text-slate-500" data-testid="explain-notice">{data.notice}</p>
          </>)}
        </div>
      )}
    </section>
  );
}
